import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { EntityTypesSupportingRoutes } from "@mapmycustomers/shared/types/entity";

import { Wizard } from "@app/component/wizard";
import { setAddRecordsVisibility, submitAddRecords } from "@app/scene/map/store/leadFinder/actions";
import { getAddRecordsEntityType } from "@app/scene/map/store/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import styles from "./AddRecordsWizard.module.scss";
import AddRecordsWizardState from "./AddRecordsWizardState";
import steps from "./steps";

interface Props {
  entityType: EntityTypesSupportingRoutes;
  setAddRecordsVisibility: typeof setAddRecordsVisibility;
  submitAddRecords: typeof submitAddRecords.request;
}

const AddRecordsWizard: React.FC<Props> = ({
  entityType,
  setAddRecordsVisibility,
  submitAddRecords,
}) => {
  const analytics = useAnalytics();

  const initialState: AddRecordsWizardState = useMemo(
    () => ({
      entityType,
    }),
    [entityType]
  );

  const handleHide = useCallback(
    (finished: boolean) => {
      if (!finished) {
        setAddRecordsVisibility({ visible: false });
      }
    },
    [setAddRecordsVisibility]
  );

  const handleFinish = useCallback(
    ({
      customFields,
      entity,
      entityType,
      groupIds,
      layout,
      personId,
      routeIdsOrRoutePayloads,
    }: AddRecordsWizardState) => {
      submitAddRecords({
        customFields,
        entity: entity!,
        entityType,
        groupIds,
        layoutId: layout!.id,
        personId,
        routeIdsOrRoutePayloads,
      });

      analytics.completed("", {
        type: getEntityTypeAnalyticsName(entityType, false, true),
      });
    },
    [analytics, submitAddRecords]
  );

  return (
    <Wizard
      className={styles.container}
      initialState={initialState}
      onFinish={handleFinish}
      onHide={handleHide}
      steps={steps}
      width="auto"
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  entityType: getAddRecordsEntityType(state),
});

const mapDispatchToProps = {
  setAddRecordsVisibility,
  submitAddRecords: submitAddRecords.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(AddRecordsWizard);
